import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";

import Marb from "../../assests/images/marseg.svg";
import MarlboroRed from "../../assests/images/marlboro_red.svg";
import marlboroGold from "../../assests/images/marlboro_gold.svg";
import MarlboroGoldAdvance from "../../assests/images/marlboro-gold-advance.svg";
import marlboroFuseBeyond from "../../assests/images/marlboro-fuse-beyond.svg";
import MarlborocloveMix from "../../assests/images/marlboro-clove-mix.svg";
import MarlboroVistaForestFusion from "../../assests/images/marlboro-vista-forest-fusion.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSfaData } from "../../utils/apis";
import { KEY_AUTH } from "../../utils/contants";

type VariantProgressListProps = { contactId?: string | null };

const variantImages: Record<string, string> = {
  "Marlboro Red": MarlboroRed,
  "Marlboro Gold": marlboroGold,
  "Marlboro Gold Advance": MarlboroGoldAdvance,
  "Marlboro Fuse Beyond": marlboroFuseBeyond,
  "Marlboro Clove Mix": MarlborocloveMix,
  "Marlboro Vista Forest Fusion": MarlboroVistaForestFusion,
};

interface Variant {
  name: string;
  actual: number;
  target: number;
  img_path?: string;
  group?: string;
  clubTarget?: number; // club-level target_count (individual variant target may be 0)
}

interface GroupItem {
  type: "group";
  name: string;
  clubTarget: number; // from club.target_count
  items: Variant[];
}

interface SingleItem extends Variant {
  type: "single";
}

const VariantProgressList: React.FC<VariantProgressListProps> = ({ contactId }) => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<number>(0); // 0 = show target+actual, 1 = actual only
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const now = new Date();
  const selectedMonth = searchParams.get("month") || now.toLocaleString("en-US", { month: "long" });
  const selectedYear = searchParams.get("year") || String(now.getFullYear());

  useEffect(() => {
    if (!contactId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(KEY_AUTH) || "";
        const res = await getSfaData(token, selectedMonth, selectedYear, contactId);
        const json = res?.data ?? res;

        if (!json || !Array.isArray(json) || !json[0]) return;

        const topData = json[0];
        const showActualOnly = Number(topData.show_target_only) === 1;
        setViewMode(showActualOnly ? 1 : 0);

        const mappedVariants: Variant[] = [];

        // Standalone variants
        (topData.standalone_variants || []).forEach((v: any) => {
          mappedVariants.push({
            name: v.name,
            actual: Number(v.actual) || 0,
            target: Number(v.target) || 0,
            img_path: v.img_path || "",
          });
        });

        // Club variants — always grouped by club_name.
        // When showActualOnly: variants have no target field; progress bar hidden by viewMode.
        (topData.clubs || []).forEach((club: any) => {
          const clubTarget = Number(club.target_count) || 0;
          (club.variants || []).forEach((v: any) => {
            mappedVariants.push({
              name: v.name,
              actual: Number(v.actual) || 0,
              target: Number(v.target) || 0,
              img_path: v.img_path || "",
              group: club.club_name,
              clubTarget,
            });
          });
        });

        setVariants(mappedVariants);
      } catch (error) {
        console.error("Error fetching TOP data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contactId, selectedMonth, selectedYear]);

  // Build grouped display list
  const groupedData: (GroupItem | SingleItem)[] = [];
  const groupMap: Record<string, GroupItem> = {};

  variants.forEach((v) => {
    if (v.group) {
      if (!groupMap[v.group]) {
        groupMap[v.group] = { type: "group", name: v.group, clubTarget: v.clubTarget ?? 0, items: [] };
        groupedData.push(groupMap[v.group]);
      }
      groupMap[v.group].items.push(v);
    } else {
      groupedData.push({ type: "single", ...v });
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "#FAFAFA",
        p: 2,
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" fontWeight="bold" mb={2} sx={{ textAlign: "left" }}>
        List of variants
      </Typography>

      {groupedData.map((item, index) => {

        // GROUP BLOCK
        if (item.type === "group") {
          const totalActual = item.items.reduce((sum, v) => sum + (v.actual || 0), 0);
          // Use club-level target_count, not the sum of individual variant targets (which can be 0)
          const totalTarget = item.clubTarget || item.items.reduce((sum, v) => sum + (v.target || 0), 0);

          return (
            <Box key={index} sx={{ mb: 1.25, background: "#F5F3F0", borderRadius: 2, overflow: "hidden", boxShadow: "0 0 6px 3px #efeaea" }}>
              <Box
                onClick={() => setOpenGroup(openGroup === item.name ? null : item.name)}
                sx={{
                  backgroundColor: "#fff",
                  borderTopLeftRadius: 2,
                  borderTopRightRadius: 2,
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                  p: 1.5,
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  textAlign: "left",
                  borderBottom: "1px solid #dad1d1",
                }}
              >
                {/* LEFT SIDE */}
                <Box sx={{ width: "70%", textAlign: "left" }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ textAlign: "left", fontSize: "14px" }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "12px", color: "#6C6C6C", display: "block" }}>
                    {item.items.map((i) => i.name).join(" + ")}
                  </Typography>
                </Box>

                {/* RIGHT SIDE */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "12px", whiteSpace: "nowrap", display: "flex" }}>
                    {viewMode === 1 ? (
                      <Box component="span" sx={{ color: "rgb(70,198,90)" }}>
                        {totalActual} Packs
                      </Box>
                    ) : (
                      <>
                        <Box component="span" sx={{ color: "rgb(70,198,90)" }}>{totalActual}</Box>
                        <Box component="span"> / {totalTarget} Packs</Box>
                      </>
                    )}
                  </Typography>
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 22,
                      color: "#333",
                      transform: openGroup === item.name ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.3s",
                    }}
                  />
                </Box>
              </Box>

              {/* CHILD ITEMS */}
              {openGroup === item.name && (
                <Box sx={{ mt: 0, p: "12px" }}>
                  {item.items.map((variant, i) => {
                    const image = variant.img_path || variantImages[variant.name] || Marb;
                    // Individual variant may have target=0; use club target split equally across variants
                    const effectiveTarget = variant.target > 0
                      ? variant.target
                      : (variant.clubTarget ?? 0) / (item.items.length || 1);
                    const progressPercent =
                      effectiveTarget > 0 ? Math.min((variant.actual / effectiveTarget) * 100, 100) : 0;
                    const color = progressPercent >= 100 ? "#46C65A" : "#FCC003";

                    return (
                      <Box
                        key={i}
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 2,
                          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                          p: 1.5,
                          mb: 2,
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        <Stack spacing={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ width: 48, height: 21, borderRadius: "0px", background: "transparent" }}>
                                <img src={image} style={{ width: "100%" }} />
                              </Avatar>
                              <Typography fontWeight="bold" sx={{ textAlign: "left", fontSize: "14px" }}>
                                {variant.name}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "12px", display: "flex", width: "30%", justifyContent: "end" }}>
                              <span style={{ color, display: "block", fontWeight: 700 }}>
                                {variant.actual} Packs
                              </span>
                            </Typography>
                          </Box>
                          {/* {viewMode === 0 && (
                            <Box sx={{ position: "relative", height: 6, mt: "15px !important" }}>
                              <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                  height: 4,
                                  borderRadius: 3,
                                  backgroundColor: "#eee",
                                  "& .MuiLinearProgress-bar": { backgroundColor: color },
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "-3.5px",
                                  left: `${Math.min(progressPercent, 100)}%`,
                                  transform: "translateX(-50%)",
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: color,
                                  border: "3px solid white",
                                  boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                                  zIndex: 1,
                                }}
                              />
                            </Box>
                          )} */}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        }

        // SINGLE ITEM
        const variant = item as SingleItem;
        const variantImage = variant.img_path || variantImages[variant.name] || Marb;
        const color = viewMode === 0 ? "#46C65A" : "#000";

        return (
          <Box
            key={index}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
              p: 1.5,
              mb: 2,
            }}
          >
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 48, height: 21, borderRadius: "0px", background: "transparent" }}>
                    <img src={variantImage} style={{ width: "100%" }} />
                  </Avatar>
                  <Typography fontWeight="bold" sx={{ fontSize: "14px", textAlign: "left", lineHeight: "1rem" }}>
                    {variant.name}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "12px", display: "flex", minWidth: "90px", justifyContent: "end" }}>
                  {viewMode === 1 ? (
                    <span style={{ color, fontWeight: 700 }}>
                      {variant.actual} Packs
                    </span>
                  ) : (
                    <>
                      <span style={{ color, fontWeight: 700, paddingRight:"4px" }}>{variant.actual}</span>
                      <span> Packs</span>
                    </>
                  )}
                </Typography>
              </Box>
              {/* {viewMode === 0 && (
                <Box sx={{ position: "relative", height: 6, mt: "15px !important" }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                      height: 4,
                      borderRadius: 3,
                      backgroundColor: "#eee",
                      "& .MuiLinearProgress-bar": { backgroundColor: color },
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-3.5px",
                      left: `${Math.min(progressPercent, 100)}%`,
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: color,
                      border: "3px solid white",
                      boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                      zIndex: 1,
                    }}
                  />
                </Box>
              )} */}
            </Stack>
          </Box>
        );
      })}

      <Button
        variant="text"
        fullWidth
        onClick={() => navigate("/top-survey")}
        sx={{
          mt: 1,
          color: "#fff",
          fontWeight: 500,
          background: "#C8172F",
          height: "44px",
          borderRadius: "10px",
          "&:hover": { background: "#C8172F", color:"#fff" },
        }}
      >
        Start Self Tracking
      </Button>
    </Box>
  );
};

export default VariantProgressList;
